import React, { useState, useEffect } from 'react';
import { humanize, titleize } from 'underscore.string';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import CargomaticModal from 'components/v2/modal';
import CargomaticInput from 'components/v2/input';
import CargomaticRadio from 'components/v2/forms/Radio';
import { sluggify } from 'utils/localization/utils';

const ReviewBankingModal = ({
  partnerFactoringCompanyName,
  partnerWithFactoringCompany,
  sensitiveDocumentData,
  show,
  onClose,
  onConfirmDetails,
  onDeclineDetails,
  onGetVerificationData,
}) => {
  const { t } = useTranslation();
  const [partnerCompany, setPartnerCompany] = useState(
    partnerFactoringCompanyName || '',
  );
  const [partnerCompanyChecked, setPartnerCompanyChecked] = useState(
    partnerWithFactoringCompany || false,
  );
  const [documentFetched, setDocumentFetched] = useState(false);

  const getVerificationData = async () => {
    await onGetVerificationData().catch((e) => {
      return console.log(e);
    });
    setDocumentFetched(true);
  };

  useEffect(() => {
    getVerificationData();
  }, []);

  const handlePartnerCompanyCheckboxChange = () => {
    setPartnerCompanyChecked((prev) => !prev);
  };

  const handlePartnerCompanyChange = (partnerCompany) => {
    setPartnerCompany(partnerCompany || '');
  };

  const handleConfirmBankingDetails = () => {
    onConfirmDetails();
    onClose();
  };

  const handleDeclineBankingDetails = () => {
    onDeclineDetails();
    onClose();
  };

  const openAllDocumentLinks = () => {
    if (sensitiveDocumentData) {
      const documentLinks = sensitiveDocumentData.reduce(
        (documentArray, currentDoc) => {
          return currentDoc.url
            ? documentArray.concat(currentDoc.url)
            : documentArray;
        },
        [],
      );
      documentLinks.forEach((documentLink) => {
        window.open(documentLink, '_blank');
      });
    }
  };

  const getVerificationDocumentList = () => {
    if (!sensitiveDocumentData || sensitiveDocumentData.length === 0) {
      if (documentFetched) {
        return <h5>{t('NO_DOCUMENTS_UPLOADED')}</h5>;
      }
      return <h5>{t('FETCHING_DOCUMENTS')}...</h5>;
    }

    const groupedDocuments = {
      w9: [],
      voided_check: [],
      notice_of_assignment: [],
    };
    sensitiveDocumentData.forEach((document) => {
      if (document.metadata?.document_type === 'w9') {
        groupedDocuments.w9.push(document);
      } else if (document.metadata?.document_type === 'voided_check') {
        groupedDocuments.voided_check.push(document);
      } else if (document.metadata?.document_type === 'notice_of_assignment') {
        groupedDocuments.notice_of_assignment.push(document);
      }
    });

    return (
      <div className="grid grid-cols-2 gap-4 justify-items-start mx-3">
        {Object.keys(groupedDocuments).map((documentType) => {
          if (!groupedDocuments[documentType].length) return null;
          return (
            <div key={documentType}>
              <h6 className="my-3">{t(sluggify(humanize(documentType)))}</h6>
              <div className="grid content-center gap-4 grid-cols-2 justify-items-start">
                {groupedDocuments[documentType].map(
                  (document, documentIndex) => {
                    return (
                      <a
                        key={documentIndex}
                        href={document.url || ''}
                        target="_blank"
                        rel="noreferrer"
                      >{`Document ${documentIndex + 1}`}</a>
                    );
                  },
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const getBody = () => {
    return (
      <>
        {partnerCompanyChecked && (
          <div className="max-w-sm pb-2 m-3 form-label">
            <CargomaticRadio
              id="partner_company_checkbox"
              className="my-3"
              checked={partnerCompanyChecked}
              disabled
              name="partnerCompanyRadio"
              value={partnerCompanyChecked}
              onChange={() => {}}
            >
              {t('PARTNER_WITH_FACTORING_COMPANY')}
            </CargomaticRadio>
            <CargomaticInput
              name="partnerCompany"
              value={partnerCompany}
              onChange={handlePartnerCompanyChange}
              type="text"
              className="mb-4 mx-4"
              placeholder={t('PARTNER_COMPANY_NAME')}
              disabled
            />
          </div>
        )}
        <div className="flex mx-3 gap-4 items-center">
          <h4>{t('DOCUMENTS_UPLOADED')}</h4>
          {sensitiveDocumentData?.length && (
            <a
              className="underline cursor-pointer"
              onClick={openAllDocumentLinks}
            >
              {t('OPEN_ALL_DOCUMENTS')}
            </a>
          )}
        </div>
        {getVerificationDocumentList()}
      </>
    );
  };

  const getFooter = () => {
    return (
      <div className="flex justify-end items-center space-x-2">
        <button
          className="button button-green"
          onClick={handleConfirmBankingDetails}
        >
          {t('CONFIRM_BANKING_INFORMATION')}
        </button>
        <button
          className="button button-secondary"
          onClick={handleDeclineBankingDetails}
        >
          {t('DECLINE')}
        </button>
      </div>
    );
  };

  return (
    <CargomaticModal
      show={show}
      title={titleize(t('BANKING_INFORMATION'))}
      body={getBody()}
      footer={getFooter()}
      onHide={onClose}
    />
  );
};

ReviewBankingModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  partnerWithFactoringCompany: PropTypes.bool,
  partnerFactoringCompanyName: PropTypes.string,
  onConfirmDetails: PropTypes.func.isRequired,
  onDeclineDetails: PropTypes.func.isRequired,
  sensitiveDocumentData: PropTypes.array,
  onGetVerificationData: PropTypes.func,
};

export default ReviewBankingModal;
